package dev.lingvo.site.ds

import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.compose.ui.graphics.Color

/**
 * The colours a chart is allowed to use.
 *
 * **Validated, not chosen by eye** — and the previous set was not, whatever its comment claimed.
 * Measured in OKLab through the Machado 2009 severity-1.0 matrices, the palette that shipped put
 * series 2 and series 4 at **ΔE 1.0** for a deuteranope, in both modes. They were the same colour.
 * The old note said the order kept confusable hues non-adjacent, which is no help at all in a line
 * chart that draws every series at once.
 *
 * These sets clear **ΔE 9.2 (Day) and 9.7 (Night)** across normal, protan, deutan and tritan
 * vision, every pair, not just adjacent ones — nine to ten times what shipped — while every series
 * clears 3:1 against both the card and the page it can sit on.
 *
 * They are not pushed further because they do not have to be. Colour is one of three channels
 * here: see [dash] and [marker]. Chasing ΔE 16 with colour alone costs a coherent palette — the
 * sets that score that high are electric mint beside two oranges — and it is unnecessary once a
 * reader who cannot separate two fills can separate two dashes.
 *
 * Neither set contains the accent. `$p-accent` means selection or the primary action, and a data
 * mark is neither. Ochre returns as series 2 precisely because Polyglot retires it as the brand
 * colour, which frees it to be data.
 *
 * Dark is not an inversion of light: it is its own selection against the dark ground, because a
 * hue that reads correctly on cream is either invisible or garish on ink.
 */
object ChartPalette {

    /** Assign in order. A sixth series is never a generated hue — it folds into "Other". */
    fun categorical(colorMode: ColorMode): List<Color> = if (colorMode.isDark) DARK else LIGHT

    private val LIGHT = listOf(
        Color.rgb(0x1C487B), // slate blue
        Color.rgb(0xBA7424), // ochre — the retired brand mark, now free to be data
        Color.rgb(0x4C9875), // green
        Color.rgb(0x78214B), // plum
        Color.rgb(0xA84538), // terracotta
    )

    private val DARK = listOf(
        Color.rgb(0x89BEE8),
        Color.rgb(0xA16508),
        Color.rgb(0x19886A),
        Color.rgb(0xBE77AA),
        Color.rgb(0xFD978A),
    )

    /**
     * Translation state is not identity — it is a progression with a verdict at the end, so it
     * wears the status palette and always ships with its label. These are reserved: a fifth series
     * never borrows "approved green".
     *
     * **This is the one home for a state hue, and it did not used to be.** The same "translated"
     * state was painted three different colours by three components that plot the same
     * [LProgressCounts]: grey here in `LProgressBar`, `info` blue in `LProgressRing`, and the
     * accent in this function. A reader moving between a project card and an insights bar saw
     * three colours for one fact and had no way to know it was one fact.
     *
     * `TRANSLATED` is `textMuted` rather than the accent because of house rule 3 — `$p-accent`
     * means selection or the primary action, never a status — and rather than `info` because
     * `LProgressBar` had already retired that blue by name: a slate-blue "translated" sits next to
     * the accent and reads as a selection. Grey is the correct statement anyway: translated is the
     * *absence* of a verdict, and it is the only state here whose word has to carry it.
     *
     * Callers own the treatment, not the hue. A filled cell washes this over its surface; a 4px
     * ring arc strokes it solid. Both start here.
     */
    fun forState(colorMode: ColorMode, state: ChartState): Color {
        val palette = colorMode.toSitePalette()
        return when (state) {
            ChartState.APPROVED -> palette.success
            ChartState.TRANSLATED -> palette.textMuted
            ChartState.NEEDS_REVIEW -> palette.warning
            ChartState.BLOCKED -> palette.danger
            ChartState.UNTRANSLATED -> palette.borderStrong
        }
    }

    /**
     * How much of a state hue a *filled* mark carries when it sits under its own boundary.
     *
     * `LProgressBar` paints a cell as a wash plus a 1px edge in the same hue, so the wash has to
     * stay light enough for the edge to read against it. 28% is what shipped and is kept exactly;
     * it lives here so the wash and [forState] cannot drift apart.
     */
    const val STATE_FILL_TINT = 0.28f

    /** A wash, never a saturated block. */
    const val AREA_OPACITY = 0.10

    /** Thin marks: the data is the only thing allowed to be loud. */
    const val LINE_WIDTH = 2.0
    const val MARKER_RADIUS = 4.0
    const val MAX_BAR_THICKNESS = 24.0

    /** Surface-coloured separation, so touching marks never need a border drawn around them. */
    const val SURFACE_GAP = 2.0

    /**
     * The second channel, and the reason the hues above are allowed to stay on-brand.
     *
     * Colour alone cannot carry five categories. Measured with the Machado 2009 severity-1.0
     * matrices in OKLab, any five hues that clear 3:1 on this paper sit within ΔE 16 of each
     * other for a dichromat at best — and any five that also look like they belong to one
     * warm-paper palette sit within ΔE 7. The set that shipped was at **1.0**: series 2 and 4
     * were the same colour for a deuteranope, in both modes.
     *
     * That is not a palette problem, it is a channel problem. One in twelve men has a colour
     * vision deficiency, so a chart whose only key is hue is a chart a twelfth of the audience
     * cannot read. Every series therefore carries a dash and a marker shape as well, and both
     * are repeated in the legend so the mapping is learnable rather than decoded.
     *
     * Index by series, wrapping. Series one is solid with a round marker: the commonest chart
     * has one series, and it should not look encoded.
     */
    fun dash(seriesIndex: Int): String? =
        DASHES[seriesIndex % DASHES.size]

    private val DASHES = listOf(null, "7 4", "1.5 3.5", "11 3 2 3", "5 2.5")

    fun marker(seriesIndex: Int): MarkerShape =
        MarkerShape.entries[seriesIndex % MarkerShape.entries.size]
}

/**
 * Shapes carry identity where colour cannot.
 *
 * Chosen so the silhouettes stay distinct at the 4px radius a chart marker actually renders at —
 * round, square and triangle are separable at that size in a way that, say, a pentagon and a
 * hexagon are not.
 */
enum class MarkerShape { CIRCLE, SQUARE, TRIANGLE, DIAMOND, PLUS }

enum class ChartState(val label: String) {
    APPROVED("Approved"),
    TRANSLATED("Translated"),
    NEEDS_REVIEW("Needs review"),
    BLOCKED("Blocked"),
    UNTRANSLATED("Untranslated"),
}
